/* ───────────────────────────────────────────────────────────────
   Client-side access to the blog content generated at build time.

   Nothing here parses Markdown — scripts/build-blog.ts does that once,
   at build time, and writes plain HTML into public/blog/. The browser
   only ever fetches finished fragments, so no Markdown parser ships to
   the client.

   Generated layout:
     public/blog/index.json          list of PostMeta, newest first
     public/blog/<slug>/index.html   standalone page (crawlers, direct links)
     public/blog/<slug>/body.html    <article> fragment (this pane)
   ─────────────────────────────────────────────────────────────── */

export interface PostMeta {
  slug: string;
  title: string;
  /** ISO date, e.g. "2026-08-09". */
  date: string;
  description: string;
  tags: string[];
  /** Estimated reading time in minutes. */
  readingMinutes: number;
}

let indexCache: Promise<PostMeta[]> | null = null;
const bodyCache = new Map<string, string>();

/** Fetch the post index. Cached for the session; safe to call repeatedly. */
export function loadIndex(): Promise<PostMeta[]> {
  indexCache ??= fetch("/blog/index.json")
    .then((r) => {
      if (!r.ok) throw new Error(`blog index unavailable (HTTP ${r.status})`);
      return r.json() as Promise<PostMeta[]>;
    })
    .catch((err) => {
      indexCache = null; // let a later call retry rather than cache the failure
      throw err;
    });
  return indexCache;
}

/** Fetch one post's rendered <article> fragment. */
export async function loadPostBody(slug: string): Promise<string> {
  const cached = bodyCache.get(slug);
  if (cached !== undefined) return cached;

  const res = await fetch(`/blog/${encodeURIComponent(slug)}/body.html`);
  if (!res.ok) throw new Error(`post "${slug}" not found (HTTP ${res.status})`);
  const html = await res.text();
  bodyCache.set(slug, html);
  return html;
}

export async function findPost(slug: string): Promise<PostMeta | undefined> {
  const posts = await loadIndex();
  return posts.find((p) => p.slug === slug);
}

/** "2026-08-09" → "09 Aug 2026". Keeps the terminal's terse, uppercase feel. */
export function formatDate(iso: string): string {
  const d = new Date(iso + "T00:00:00Z");
  if (Number.isNaN(d.getTime())) return iso;
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${String(d.getUTCDate()).padStart(2, "0")} ${months[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}
